import { DeleteOutlined, MenuOutlined, SlidersOutlined } from '@ant-design/icons';
import { Badge, Button } from 'antd';
import classNames from 'classnames';
import { notEqual } from 'ramda-adjunct';
import React, { useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { equals, not, pathOr, prop, propOr, isNotNil, isNotEmpty } from 'ramda';
import { Components } from '@fasstech/flexiget';
import { mapIndexed } from 'ramda-adjunct';
import { originFromItemPath } from '../../../../../../lib/sessionHistory';
import DocumentRules from '../../../rules/DocumentRules';
import PaddingInfo from '../PaddingInfo';
import { useDocumentContext } from '../../../../../../context/Document';
import colors from '../../../../../../colors';
import Switch from '../../../../../../components/Switch';
import { buildCardId } from '../../helpers/highlightNavigation';
import WidgetForm from './WidgetForm';
import { filterNonEmptyRows } from './helpers/filterNonEmptyRows';

const WidgetCard = ({ rowIndex, sectionIndex, widgetType, addRowWidget, moveRowWidget, children, widgetRow, updatePaddingRow, userCanEdit }) => {
  const dragRef = useRef(null);
  const previewRef = useRef(null);

  const [{ handlerId }, drop] = useDrop({
    accept: 'CARD',
    hover (item, monitor) {
      const current = prop('current', previewRef);
      if (!current) return;
      const dragIndex = prop('index', item);
      const hoverIndex = rowIndex;
      if (equals(dragIndex, hoverIndex)) return;
      const hoverBoundingRect = current.getBoundingClientRect();
      const hoverMiddleY = (prop('bottom', hoverBoundingRect) - prop('top', hoverBoundingRect)) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = prop('y', clientOffset) - prop('top', hoverBoundingRect);
      if ((dragIndex < hoverIndex && hoverClientY < hoverMiddleY) || (dragIndex > hoverIndex && hoverClientY > hoverMiddleY)) return;
      moveRowWidget(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    collect: (monitor) => ({ handlerId: monitor.getHandlerId() })
  });

  const [, drag, preview] = useDrag({
    type: 'CARD',
    item: { index: rowIndex }
  });

  drag(dragRef);
  drop(preview(previewRef));

  return (
    <div
      id={buildCardId(sectionIndex, widgetType, rowIndex)}
      className={classNames('widget-card p-3 border rounded border-transparent hover:bg-gray-100', { 'mt-1': rowIndex > 0 })}
    >
      <PaddingInfo updatePadding={updatePaddingRow(rowIndex)} element={widgetRow} userCanEdit={userCanEdit} />
      <div ref={previewRef} className="flex" data-handler-id={handlerId}>
        {userCanEdit && (
          <MenuOutlined ref={dragRef} className="flex items-center mr-2 cursor-grab"/>
        )}
        <div className="flex flex-col w-full">
          <div className="w-full flex flex-col">
            {children}
          </div>
          {userCanEdit && (
            <button
              type="button"
              className="text-md text-flexi-primary-color font-medium w-fit bg-transparent border-none p-0 cursor-pointer"
              onClick={() => addRowWidget(rowIndex)}
            >
              + ajouter un widget sur la ligne
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const WidgetsForm = ({
  widgets,
  updateWidget,
  removeWidget,
  getWidgetVariables,
  globalVariables,
  getRulesAreValid,
  getConditionsAreValid,
  addWidgetRule,
  removeWidgetRule,
  updateWidgetRule,
  addRowWidget,
  itemPathPrefix,
  moveRowWidget,
  updateManualBreakPage,
  updatePaddingRow,
  type,
  addWidget,
  disabled,
  hasSectionBreakPage,
  setIsDynamicTableInSection,
  addManualBreakPageRule,
  removeManualBreakPageRule,
  updateManualBreakPageRule,
  setIsOpenCreateWidgetModal,
  userCanEdit
}) => {
  const { setIsOpenCreateGlobalVariableModal, variablesForRules } = useDocumentContext();
  const [showRules, setShowRules] = useState(false);
  // Direct derivation from props (no useState/useEffect): rows must exist from the
  // first render, otherwise a programmatic scroll/highlight right after mount
  // (cf. onWidgetHighlight from the previewer) cannot find the element in the DOM yet.
  const widgetsFiltered = filterNonEmptyRows(widgets);
  // itemPathPrefix = [...optional ids, 'pages', sectionIndex, type]: variable length,
  // always read from the end (cf. originFromItemPath).
  const { sectionIndex, block: widgetType } = originFromItemPath(itemPathPrefix);
  return (
    <div className="mr-2">
      {mapIndexed((widgetRow, rowIndex) => {
        const rowWidgets = propOr([], 'widgets', widgetRow);
        const canBreakPageBetween = isNotNil(widgetsFiltered[rowIndex - 1]);
        const hasBreakPage = pathOr(false, ['breakPage'], widgetRow);
        const shouldDisplayBreakPage = canBreakPageBetween || hasBreakPage;

        const displayWidgetOptions = userCanEdit && shouldDisplayBreakPage && equals('content', type);

        return (
          <div key={`selectWidgetRow${rowIndex}`}>
            {displayWidgetOptions && (
              <div className="relative my-4">
                {hasBreakPage ? (
                  <div className="flex flex-col">
                    <div className="inline-flex w-full">
                      <div className="w-full flex justify-center">
                        <hr className="absolute top-3 z-0 h-0 border-gray-200 border-dotted border-t-2 w-10/12" />
                        <span className="bg-white shadow-none w-fit relative z-10 px-3 text-xs text-flexi-grey">Saut de page manuel</span>
                      </div>
                      <div className="inline-flex space-x-4 mr-3 items-center">
                        <Badge
                          dot={isNotEmpty(prop('rulesBreakPage', widgetRow))}
                          color={isNotEmpty(prop('rulesBreakPage', widgetRow)) ? colors['flexi-primary-color'] : undefined}
                        >
                          <Components.Tooltip
                            title="Ajouter une règle"
                          >
                            <SlidersOutlined
                              onClick={() => setShowRules(not)}
                              rotate={90}
                            />
                          </Components.Tooltip>
                        </Badge>
                        {userCanEdit && (
                          <Components.Tooltip
                            title="Supprimer le saut de page"
                          >
                            <DeleteOutlined
                              onClick={() => updateManualBreakPage(rowIndex, false)}
                              className="btn-remove-breakpage hover:text-flexi-red"
                            />
                          </Components.Tooltip>
                        )}
                      </div>
                    </div>
                    {showRules && (
                      <div className="ml-4">
                        <DocumentRules
                          variables={variablesForRules}
                          rules={prop('rulesBreakPage', widgetRow)}
                          rulesAreValid={getRulesAreValid(prop('rulesBreakPage', widgetRow))}
                          getConditionsAreValid={getConditionsAreValid}
                          onAdd={() => addManualBreakPageRule(rowIndex)}
                          onRemove={removeManualBreakPageRule(rowIndex)}
                          onUpdate={updateManualBreakPageRule(rowIndex)}
                          userCanEdit={userCanEdit}
                          setIsOpenCreateGlobalVariableModal={setIsOpenCreateGlobalVariableModal}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <Components.Tooltip title="Ajouter une ligne" className="ml-3 w-fit">
                      <Button
                        size="small"
                        className="!rounded-full"
                        onClick={() => addWidget(rowIndex)}
                        disabled={disabled}
                      >
                        +
                      </Button>
                    </Components.Tooltip>
                    <Switch
                      className="mt-1 ml-3"
                      checked={hasBreakPage}
                      onChange={() => updateManualBreakPage(rowIndex, true)}
                      onClick={() => updateManualBreakPage(rowIndex, !hasBreakPage)}
                      label="Saut de page manuel"
                    />
                  </>
                )}
              </div>
            )}
            <WidgetCard
              rowIndex={rowIndex} sectionIndex={sectionIndex} widgetType={widgetType}
              addRowWidget={addRowWidget} moveRowWidget={moveRowWidget}
              widgetRow={widgetRow} updatePaddingRow={updatePaddingRow} userCanEdit={userCanEdit}
            >
              {mapIndexed(({ widgetId, rules }, index) => (
                <WidgetForm
                  key={`selectWidget${rowIndex}-${index}`}
                  index={index}
                  widgetId={widgetId}
                  itemPathPrefix={itemPathPrefix}
                  rowIndex={rowIndex}
                  widgetType={widgetType}
                  sectionIndex={sectionIndex}
                  rules={rules}
                  getWidgetVariables={getWidgetVariables}
                  globalVariables={globalVariables}
                  getRulesAreValid={getRulesAreValid}
                  getConditionsAreValid={getConditionsAreValid}
                  addWidgetRule={addWidgetRule}
                  removeWidgetRule={removeWidgetRule}
                  updateWidgetRule={updateWidgetRule}
                  updateWidget={updateWidget}
                  removeWidget={removeWidget}
                  canRemove={notEqual('content', type) || (notEqual(0, rowIndex) || widgetsFiltered.length > 1 || rowWidgets.length > 1)}
                  type={type}
                  hasSectionBreakPage={hasSectionBreakPage}
                  setIsDynamicTableInSection={setIsDynamicTableInSection}
                  setIsOpenCreateWidgetModal={setIsOpenCreateWidgetModal}
                  userCanEdit={userCanEdit}
                />
              ))(rowWidgets)}
            </WidgetCard>

          </div>
        );
      })(widgetsFiltered)}
    </div>
  );
};

export default WidgetsForm;
